// Paid-lane attribution persistence for funnel redirects.
//
// The bug this exists to fix: handleRedirection() built its outbound query
// string from the CURRENT window.location.search. Once the URL had lost its
// params by the time a divert fired, the receiving lander saw no ppcid and
// defaulted to 7 "Organic" — booking paid-lane revenue to the wrong PPCID.
// handleQueryStrings() has the same exposure: it runs once at module load, so
// a full reload on a bare URL (the ID5 TrueLink bootstrap in GTM does a
// round-trip redirect) resets ppcid to the organic default for the rest of
// the session, including the lead post.
//
// Fix: capture attribution params at entry into sessionStorage, and at
// redirect time MERGE — start from stored attribution, let anything actually
// present in the current URL win. sessionStorage is tab-scoped and survives
// the reload that module scope does not. Every access is wrapped: this is an
// enhancement, never a hard fail.

const KEY = 'rz_attribution';

// Params that identify the paid lane. Lowercase — capture is
// case-insensitive because live ad final URLs pass ?PPCID=...&CID=...
const ATTRIBUTION_PARAMS = ['ppcid', 'cid', 'fbclid', 'gclid', 'msclkid', 'tsid'];

export function readStoredAttribution() {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.sessionStorage.getItem(KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

// Capture attribution params from the current URL into sessionStorage.
// Only overwrites a stored value when the URL actually carries the param —
// an attribution-less mid-funnel URL must never erase what we learned at
// entry.
export function captureAttribution() {
    if (typeof window === 'undefined') return;
    try {
        const query = new URLSearchParams(window.location.search);
        // Case-insensitive, first occurrence wins.
        const lower = new Map();
        for (const [k, v] of query) {
            const key = k.toLowerCase();
            if (!lower.has(key) && v) lower.set(key, v);
        }
        const stored = readStoredAttribution();
        let changed = false;
        for (const p of ATTRIBUTION_PARAMS) {
            const v = lower.get(p);
            if (v && stored[p] !== v) {
                stored[p] = v;
                changed = true;
            }
        }
        if (changed) window.sessionStorage.setItem(KEY, JSON.stringify(stored));
    } catch {
        // Storage unavailable (quota/privacy mode) — callers fall back to the
        // live URL query string, which is the pre-fix behavior.
    }
}

// Build the query string for an outbound redirect: stored attribution as the
// base, overlaid with everything in the current URL (current URL wins when
// both carry a param). Returns '' or a string starting with '?'.
export function mergedRedirectSearch() {
    if (typeof window === 'undefined') return '';
    const params = new URLSearchParams(window.location.search);
    const present = new Set(Array.from(params.keys()).map((k) => k.toLowerCase()));
    for (const [k, v] of Object.entries(readStoredAttribution())) {
        if (!present.has(k)) params.append(k, v);
    }
    const s = params.toString();
    return s ? `?${s}` : '';
}
